import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger
} from '../components/ui/dropdown-menu';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '../components/ui/dialog';
import {
  Sun, Moon, User, SignOut, Lock, GearSix, MagnifyingGlass, Warning, ArrowRight
} from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';
import NotificationBell from './NotificationBell';
import CommandPalette from './CommandPalette';

export default function TopBar() {
  const { user, logout, apiCall } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [passwordForm, setPasswordForm] = useState({ current: '', newPass: '', confirm: '' });
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);

  // Global keyboard shortcuts to open the command palette
  useEffect(() => {
    function onKey(e) {
      const k = (e.key || '').toLowerCase();
      const isCmdK = (e.metaKey || e.ctrlKey) && k === 'k';
      const isCtrlSlash = e.ctrlKey && k === '/';
      if (isCmdK || isCtrlSlash) {
        e.preventDefault();
        setPaletteOpen(true);
      } else if (k === 'escape' && paletteOpen) {
        setPaletteOpen(false);
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [paletteOpen]);

  // Listen for "Change password" quick action from CommandPalette
  useEffect(() => {
    function onOpenChangePw() {
      setPasswordError('');
      setPasswordSuccess('');
      setPasswordForm({ current: '', newPass: '', confirm: '' });
      setShowPasswordDialog(true);
    }
    window.addEventListener('roofmate:open-change-password', onOpenChangePw);
    return () => window.removeEventListener('roofmate:open-change-password', onOpenChangePw);
  }, []);

  const roleLabel = (role) => {
    switch (role) {
      case 'super_admin': return 'Admin';
      case 'tenant_admin': return 'Tenant';
      default: return 'User';
    }
  };

  const initials = (name) => {
    if (!name) return 'U';
    return name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);
  };

  async function handlePasswordChange() {
    setPasswordError('');
    setPasswordSuccess('');
    if (!passwordForm.current || !passwordForm.newPass) {
      setPasswordError('All fields are required');
      return;
    }
    if (passwordForm.newPass !== passwordForm.confirm) {
      setPasswordError('New passwords do not match');
      return;
    }
    if (passwordForm.newPass.length < 6) {
      setPasswordError('New password must be at least 6 characters');
      return;
    }
    setPasswordLoading(true);
    try {
      await apiCall('post', '/auth/change-password', {
        current_password: passwordForm.current,
        new_password: passwordForm.newPass,
      });
      setPasswordSuccess('Password changed successfully');
      setPasswordForm({ current: '', newPass: '', confirm: '' });
      setTimeout(() => { setShowPasswordDialog(false); setPasswordSuccess(''); }, 1500);
    } catch (e) {
      const detail = e.response?.data?.detail;
      setPasswordError(typeof detail === 'string' ? detail : 'Failed to change password');
    } finally {
      setPasswordLoading(false);
    }
  }

  return (
    <>
      {/* Trial / Subscription Status Banner */}
      {(() => {
        if (!user?.tenant_id) return null;
        const status = user?.subscription_status;
        const hasSub = user?.has_stripe_subscription;
        const goBilling = () => navigate(user?.role === 'tenant_admin' ? '/my-billing' : '/billing');
        const upgradeNow = async () => {
          try {
            const res = await apiCall('post', '/billing/upgrade-now', { origin_url: window.location.origin });
            if (res.data?.url) window.location.href = res.data.url;
          } catch (err) {
            // Fall back to billing page if upgrade-now is unavailable (e.g. no stripe price configured)
            goBilling();
          }
        };
        // Trial countdown
        if (status === 'trialing' && !hasSub && user?.trial_ends_at) {
          const end = new Date(user.trial_ends_at);
          const daysLeft = Math.max(0, Math.ceil((end.getTime() - Date.now()) / 86400000));
          if (daysLeft <= 7) {
            const urgent = daysLeft <= 2;
            return (
              <div
                className={`px-4 py-2 text-sm flex items-center justify-center gap-3 flex-wrap ${urgent ? 'bg-amber-500/15 text-amber-700 dark:text-amber-300 border-b border-amber-300/30' : 'bg-primary/10 text-primary border-b border-primary/20'}`}
                data-testid="trial-banner"
              >
                <Warning size={16} weight="duotone" />
                <span className="font-medium">
                  {daysLeft === 0 ? 'Your free trial ends today.' : `${daysLeft} day${daysLeft === 1 ? '' : 's'} left in your free trial.`}
                </span>
                {user?.role === 'tenant_admin' && (
                  <button onClick={upgradeNow} className="inline-flex items-center gap-1 font-semibold underline hover:no-underline" data-testid="trial-banner-subscribe">
                    Subscribe now <ArrowRight size={13} weight="bold" />
                  </button>
                )}
              </div>
            );
          }
        }
        if (status === 'trial_expired' || (user?.is_locked && status !== 'active')) {
          return (
            <div className="px-4 py-2 text-sm flex items-center justify-center gap-3 bg-red-500/15 text-red-700 dark:text-red-300 border-b border-red-300/30" data-testid="trial-expired-banner">
              <Warning size={16} weight="fill" />
              <span className="font-medium">
                {status === 'trial_expired' ? 'Your free trial has ended.' : status === 'pending_payment' ? 'Complete payment to start your free trial.' : 'Your subscription is inactive.'} Some actions are disabled.
              </span>
              {user?.role === 'tenant_admin' && (
                <button onClick={upgradeNow} className="inline-flex items-center gap-1 font-semibold underline hover:no-underline" data-testid="locked-banner-subscribe">
                  {status === 'pending_payment' ? 'Add payment method' : 'Reactivate'} <ArrowRight size={13} weight="bold" />
                </button>
              )}
            </div>
          );
        }
        if (status === 'past_due') {
          return (
            <div className="px-4 py-2 text-sm flex items-center justify-center gap-3 bg-amber-500/15 text-amber-700 dark:text-amber-300 border-b border-amber-300/30" data-testid="past-due-banner">
              <Warning size={16} weight="fill" />
              <span className="font-medium">Last payment failed.</span>
              {user?.role === 'tenant_admin' && (
                <button onClick={goBilling} className="inline-flex items-center gap-1 font-semibold underline hover:no-underline">
                  Update card <ArrowRight size={13} weight="bold" />
                </button>
              )}
            </div>
          );
        }
        return null;
      })()}

      <header className="h-[62px] bg-card border-b border-border flex items-center justify-between px-4 lg:px-6 shrink-0 shadow-sm" data-testid="top-bar">
        {/* Search - hidden on mobile, spacer for hamburger */}
        <div className="flex items-center gap-2">
          <span className="lg:hidden w-10" /> {/* spacer for hamburger */}
          <button
            type="button"
            onClick={() => setPaletteOpen(true)}
            className="flex items-center gap-2 text-muted-foreground hover:text-foreground hover:bg-secondary/60 rounded-md px-2.5 py-1.5 transition-colors group"
            data-testid="topbar-search-btn"
            title="Search (Ctrl+/)"
          >
            <MagnifyingGlass size={18} className="hidden sm:block" />
            <span className="text-sm hidden sm:inline">Search</span>
            <kbd className="hidden md:inline-flex items-center gap-0.5 text-[10px] text-muted-foreground/70 bg-muted/60 border border-border rounded px-1.5 py-0.5 ml-1 group-hover:bg-muted">
              Ctrl + /
            </kbd>
          </button>
        </div>

        {/* Right actions */}
        <div className="flex items-center gap-1">
          {/* Theme Toggle */}
          <button
            onClick={toggleTheme}
            className="w-[38px] h-[38px] rounded-md flex items-center justify-center text-foreground/60 hover:text-foreground hover:bg-secondary transition-colors"
            title={theme === 'light' ? 'Dark Mode' : 'Light Mode'}
            data-testid="topbar-theme-toggle"
          >
            {theme === 'light' ? <Moon size={20} /> : <Sun size={20} />}
          </button>

          {/* Notification Bell */}
          <NotificationBell />

          {/* User Profile Dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="ml-2 flex items-center gap-2 outline-none" data-testid="topbar-user-menu">
                <div className="w-[38px] h-[38px] rounded-full bg-primary/10 border-2 border-primary flex items-center justify-center text-primary text-xs font-bold relative">
                  {initials(user?.name)}
                  <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-success rounded-full ring-2 ring-card" />
                </div>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-60 shadow-dropdown rounded-lg p-0 overflow-hidden" data-testid="user-dropdown-menu">
              {/* User Header */}
              <div className="flex items-center gap-3 p-4 bg-secondary/50">
                <div className="w-10 h-10 rounded-full bg-primary/10 border-2 border-primary flex items-center justify-center text-primary text-sm font-bold relative shrink-0">
                  {initials(user?.name)}
                  <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-success rounded-full ring-2 ring-card" />
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-semibold truncate">{user?.name || 'User'}</p>
                  <p className="text-xs text-muted-foreground truncate">{roleLabel(user?.role)}</p>
                </div>
              </div>
              <DropdownMenuSeparator className="m-0" />
              <div className="p-1">
                <DropdownMenuItem className="gap-3 px-3 py-2.5 rounded-md cursor-pointer text-[13px]" onClick={() => navigate('/profile')} data-testid="menu-my-profile">
                  <User size={18} className="text-muted-foreground" /> My Profile
                </DropdownMenuItem>
                <DropdownMenuItem
                  className="gap-3 px-3 py-2.5 rounded-md cursor-pointer text-[13px]"
                  onClick={() => { setShowPasswordDialog(true); setPasswordError(''); setPasswordSuccess(''); setPasswordForm({ current: '', newPass: '', confirm: '' }); }}
                  data-testid="menu-change-password"
                >
                  <Lock size={18} className="text-muted-foreground" /> Change Password
                </DropdownMenuItem>
                <DropdownMenuItem className="gap-3 px-3 py-2.5 rounded-md cursor-pointer text-[13px]" onClick={() => navigate('/settings')} data-testid="menu-settings">
                  <GearSix size={18} className="text-muted-foreground" /> Settings
                </DropdownMenuItem>
              </div>
              <DropdownMenuSeparator className="m-0" />
              <div className="p-1">
                <DropdownMenuItem
                  className="gap-3 px-3 py-2.5 rounded-md cursor-pointer text-[13px] text-destructive focus:text-destructive focus:bg-destructive/10"
                  onClick={logout}
                  data-testid="menu-logout"
                >
                  <SignOut size={18} /> Logout
                </DropdownMenuItem>
              </div>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      {/* Change Password Dialog */}
      <Dialog open={showPasswordDialog} onOpenChange={setShowPasswordDialog}>
        <DialogContent className="rounded-lg" data-testid="change-password-dialog">
          <DialogHeader>
            <DialogTitle className="text-lg font-semibold">Change Password</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {passwordError && (
              <div className="p-3 bg-destructive/10 border border-destructive/20 text-destructive text-sm rounded-md" data-testid="password-error">
                {passwordError}
              </div>
            )}
            {passwordSuccess && (
              <div className="p-3 bg-success/10 border border-success/20 text-success text-sm rounded-md" data-testid="password-success">
                {passwordSuccess}
              </div>
            )}
            <div className="space-y-1.5">
              <Label className="text-[13px] font-medium">Current Password</Label>
              <Input type="password" value={passwordForm.current} onChange={(e) => setPasswordForm(p => ({ ...p, current: e.target.value }))} placeholder="Enter current password" className="rounded-md h-[38px]" data-testid="current-password-input" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-[13px] font-medium">New Password</Label>
              <Input type="password" value={passwordForm.newPass} onChange={(e) => setPasswordForm(p => ({ ...p, newPass: e.target.value }))} placeholder="Enter new password" className="rounded-md h-[38px]" data-testid="new-password-input" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-[13px] font-medium">Confirm New Password</Label>
              <Input type="password" value={passwordForm.confirm} onChange={(e) => setPasswordForm(p => ({ ...p, confirm: e.target.value }))} placeholder="Confirm new password" className="rounded-md h-[38px]" data-testid="confirm-password-input" />
            </div>
          </div>
          <DialogFooter>
            <Button onClick={handlePasswordChange} disabled={passwordLoading} className="rounded-md" data-testid="save-password-btn">
              {passwordLoading ? 'Saving...' : 'Change Password'}
            </Button>
            <Button variant="outline" onClick={() => setShowPasswordDialog(false)} className="rounded-md">Cancel</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Command Palette / Search */}
      <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} />
    </>
  );
}
